import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Switch, Checkbox } from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import _ from "lodash";
import moment from "moment";

import SelectedMachinesTable from "../../../formControl/selectedMachinesTable";
import { Spinner } from "../../../../components/Layout";
import { Button } from "@deere/fuel-react";
import { reportPickup } from "../../api";

import { AUDIT_DIALOG_TEXT } from '../../utility/constants';
import { WITHOUT_DECLARATION_WARNING_TEXT } from '../../utility/constants';
import { ARRIVAL_DEVIATION_WARNING_TEXT } from '../../utility/constants';
import { 
  CODE_BLUE, CODE_YELLOW, CODE_GREEN, GREEN, YELLOW, BLUE, 
  CODE_JD_LINK, CODE_MAINCHAIN, CODE_AVIS, CODE_RED, RED, CODE_FORWARDER 
} from "../../utility/constants";

const AvisReportPickUpDialog = (props) => {
  const { open, handleClose, selectedMachinesForReporting, getTableData, userData } = props;

  // Simplified state management - combined related states
  const [state, setState] = useState({
    departureDate: undefined,
    arrivalDateSwitchState: false,
    isDeclarationChecked: false,
    startSpinner: false,
    openDeclarationConfirmation: false,
    declarationDialogMessage: '',
    contractDevationDialogMessage: ''
  });

  // Helper functions moved inline for simplicity
  const getMinDate = () => new Date(new Date().setMonth(new Date().getMonth() - 6)).toISOString().slice(0, 10);
  
  const getDepartureColor = () => {
    if (!selectedMachinesForReporting[0]) return "";
    const colorCode = selectedMachinesForReporting[0].colorDepartureDate;
    return colorCode === GREEN ? CODE_GREEN : colorCode === BLUE ? CODE_BLUE : colorCode === YELLOW ? CODE_YELLOW : colorCode === RED ? CODE_RED : "";
  };

  const getHoverText = () => {
    const color = getDepartureColor();
    return color === CODE_GREEN ? CODE_JD_LINK : color === CODE_BLUE ? CODE_MAINCHAIN : color === CODE_YELLOW ? CODE_AVIS : color === CODE_RED ? CODE_FORWARDER : "";
  };

  // Simplified state updates
  const updateState = (updates) => setState(prev => ({ ...prev, ...updates }));

  // Extract commonly used state values
  const { arrivalDateSwitchState, startSpinner, openDeclarationConfirmation, 
          declarationDialogMessage, contractDevationDialogMessage } = state;

  // Form handling
  const handleFormChange = (event) => updateState({ departureDate: event.target.value });
  const handleArrivalDateSwitch = (e) => updateState({ arrivalDateSwitchState: e.target.checked });
  const handleCheckboxChange = (event) => updateState({ isDeclarationChecked: event.target.checked });

  // Missing handler functions
  const handleCloseSpinner = () => updateState({ startSpinner: false });
  const handleCloseDeclarationConfirmationBox = () => updateState({ openDeclarationConfirmation: false });
  const handleDeclarationYes = () => {
    updateState({ openDeclarationConfirmation: false });
    createAndSendRequest(selectedMachinesForReporting);
  };

  const handleArrivalDateChange = (orderPathId, arrivalDate) => {
    const machineIndex = selectedMachinesForReporting.findIndex(m => m.orderPathId === orderPathId);
    if (machineIndex !== -1) {
      selectedMachinesForReporting[machineIndex].finalArrivalDate = arrivalDate;
    }
  };

  // Simplified submit logic with validation
  const handleSubmit = async (e) => {
    updateState({ startSpinner: true });

    const departureDate = state.departureDate || e[0].departureDate;
    if (!departureDate) {
      toast.error("Departure date is empty");
      updateState({ startSpinner: false });
      return;
    }

    let allDatesCount = 0;
    let deviationReachedOrders = [];

    if (state.arrivalDateSwitchState) {
      const emptyDatesCount = selectedMachinesForReporting.filter(m => !m.finalArrivalDate).length;
      if (emptyDatesCount === selectedMachinesForReporting.length) {
        toast.error("Arrival dates are empty");
        updateState({ startSpinner: false });
        return;
      }
    }

    // Calculate dates and deviations
    selectedMachinesForReporting.forEach(machine => {
      if (machine.finalArrivalDate) {
        allDatesCount++;
        
        if (machine.contractDays) {
          const daysDiff = Math.round((new Date(machine.finalArrivalDate) - new Date(departureDate)) / (1000 * 3600 * 24));
          const contractDaysDiff = daysDiff - machine.contractDays;
          const maxAllowed = Math.max(5, machine.contractDays / 2);

          if (contractDaysDiff > maxAllowed) {
            deviationReachedOrders.push(`${machine.zcomar} : ${contractDaysDiff} days `);
          }
        }
      }
    });

    // Declaration validations
    if (state.isDeclarationChecked && (!state.arrivalDateSwitchState || allDatesCount !== selectedMachinesForReporting.length)) {
      const errorMsg = !state.arrivalDateSwitchState 
        ? "Can not make declaration when arrival dates switch is not enabled"
        : "Can not make declaration when arrival dates for all machines are not available";
      toast.error(errorMsg);
      updateState({ startSpinner: false });
      return;
    }

    // Show confirmation if needed, otherwise proceed
    const needsDeclarationWarning = allDatesCount === selectedMachinesForReporting.length && !state.isDeclarationChecked;
    const hasDeviations = deviationReachedOrders.length > 0;

    if (needsDeclarationWarning || hasDeviations) {
      updateState({
        declarationDialogMessage: needsDeclarationWarning ? WITHOUT_DECLARATION_WARNING_TEXT : "",
        contractDevationDialogMessage: hasDeviations ? ARRIVAL_DEVIATION_WARNING_TEXT.replace('{comar}', deviationReachedOrders) : "",
        openDeclarationConfirmation: true,
        startSpinner: false
      });
    } else {
      createAndSendRequest(e);
      updateState({ startSpinner: false });
    }
  };

  const createAndSendRequest = (e) => {
    updateState({ startSpinner: true });
    
    const orderPathId = selectedMachinesForReporting.map(m => m.orderPathId);
    const orderPathArrivalDateMap = new Map();
    
    selectedMachinesForReporting.forEach(machine => {
      orderPathArrivalDateMap.set(machine.orderPathId, 
        state.arrivalDateSwitchState ? machine.finalArrivalDate : null);
    });

    const reqObject = {
      orderPathId,
      buildId: e[0].buildId,
      departureDate: state.departureDate || e[0].departureDate,
      arrivalDateMap: Object.fromEntries(orderPathArrivalDateMap),
      userId: userData.userId,
      userName: userData.userName,
      userEmail: userData.email,
      isDeclarationBoxChecked: state.isDeclarationChecked
    };

    if (!reqObject.departureDate) {
      toast.error("Departure date is empty");
      updateState({ startSpinner: false });
      return;
    }

    reportPickup(reqObject).then((response) => {
      updateState({ startSpinner: false, arrivalDateSwitchState: false });
      
      if (response.httpResponse === 200) {
        toast.success(response.message);
      } else if (response.httpResponse === 208) {
        toast.info(response.message);
      } else {
        toast.error(response.message);
      }
      
      handleClose();
      getTableData();
    });
  };

  // Simplified useEffect - only reset when dialog opens
  useEffect(() => {
    if (open) {
      setState({
        departureDate: undefined,
        arrivalDateSwitchState: false,
        isDeclarationChecked: false,
        startSpinner: false,
        openDeclarationConfirmation: false,
        declarationDialogMessage: '',
        contractDevationDialogMessage: ''
      });
    }
  }, [open]);

  return (
    <>
      <div className="w-[90%]">
        {/* Main Dialog */}
        <Dialog
          id="avis-dialog1"
          open={open}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          maxWidth="md"
        >
          <DialogTitle id="alert-dialog-title1">{"Report Pick Up"}</DialogTitle>
          <DialogContent id="avis-dialog-content1">
            
            {/* Form Section */}
            <div className="py-2 mb-4">
              <div className="flex flex-row items-center gap-10">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium whitespace-nowrap">
                    Departure Date :
                  </label>
                  <input
                    name="departureDate"
                    type="date"
                    defaultValue={(null != selectedMachinesForReporting[0]?.departureDate && !_.isEmpty(selectedMachinesForReporting[0]?.departureDate)) ? 
                      moment(selectedMachinesForReporting[0].departureDate).format("YYYY-MM-DD") : null}
                    onKeyDown={(e) => e.preventDefault()}
                    min={getMinDate()}
                    style={{ backgroundColor: getDepartureColor() }}
                    onChange={handleFormChange}
                    max={new Date().toISOString().slice(0, 10)}
                    title={getHoverText()}
                    className="text-xs border border-gray-300 rounded px-2 py-1.5"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium whitespace-nowrap">
                    Report Arrival Date :
                  </label>
                  <Switch 
                    name="notifications" 
                    onChange={handleArrivalDateSwitch}
                    size="small"
                  />
                </div>
              </div>
            </div>

            {/* Table Section */}
            <div className="mb-4">
              <SelectedMachinesTable
                selectedMachinesForBuild={selectedMachinesForReporting}
                arrivalDateSwitchState={arrivalDateSwitchState}
                caller={"ReportPickup"}
                onArrivalDateChange={handleArrivalDateChange}
              />
            </div>

            {/* Declaration Section */}
            <div className="py-2">
              <div className="flex items-start gap-2 mb-2">
                <label className="text-xs font-medium min-w-fit pt-2">
                  Declaration :
                </label>
                <Checkbox
                  onChange={handleCheckboxChange}
                  inputProps={{ 'aria-label': 'controlled' }}
                  size="small"
                  sx={{ padding: '4px' }}
                />
              </div>

              <textarea
                id="avis-form-add-info-textarea"
                placeholder=""
                className="text-xs p-2 w-[650px] border-2 border-gray-300 rounded outline-none resize-y ml-22"
                name="declarationtext"
                rows={3}
                disabled={true}
                value={AUDIT_DIALOG_TEXT}
                maxLength="400"
              />
            </div>

          </DialogContent>
          
          <DialogActions
            id="avis-dialog-actions1"
            style={{ justifyContent: "center" }}
          >
            <Button
              id="reportPickup-apply-btn"
              variant="primary"
              onClick={() => handleSubmit(selectedMachinesForReporting)}
            >
              Report
            </Button>
            <Button
              id="avis-cancel-btn"
              variant="secondary"
              onClick={handleClose}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* Loading Spinner Dialog */}
        <Dialog
          id="reportPickup-dialog3"
          open={startSpinner}
          onClose={handleCloseSpinner}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          maxWidth="sm"
        >
          <DialogTitle id="alert-dialog-title3"></DialogTitle>
          <DialogContent id="avis-dialog-content3">
            <div
              id="reportPickup-dialog-spinner"
              className="d-flex w-100 justify-content-center align-items-center"
            >
              <Spinner
                accessibilityLabel="Loading"
                className="uxf-spinner-border-lg"
                animation="border"
              />
            </div>
          </DialogContent>
        </Dialog>

        {/* Confirmation Dialog */}
        <Dialog
          id="avis-dialog2"
          open={openDeclarationConfirmation}
          onClose={handleCloseDeclarationConfirmationBox}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          maxWidth="md"
        >
          <DialogTitle id="alert-dialog-title2">
            {"Report Pickup"}
          </DialogTitle>
          <DialogContent id="avis-dialog-content2">
            <h6>{declarationDialogMessage}</h6>
            <h6>{contractDevationDialogMessage}</h6>
          </DialogContent>
          <DialogActions id="avis-dialog-actions2" className="justify-center">
            <Button
              id="avis-dialog-delete-so-yes"
              variant="primary"
              onClick={handleDeclarationYes}
            >
              Yes
            </Button>
            <Button
              id="avis-dialog-delete-so-no"
              variant="secondary"
              onClick={handleCloseDeclarationConfirmationBox}
            >
              No
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
};

export default AvisReportPickUpDialog;
